import { execFile } from "node:child_process";
import { promisify } from "node:util";

import type { WorkspaceChangeScanner, WorkspaceChangeSnapshotEntry } from "@dotobokuri/fleet-carriers";

const execFileAsync = promisify(execFile);
const GIT_STATUS_TIMEOUT_MS = 4_000;
const GIT_STATUS_MAX_BUFFER = 1024 * 1024;
const GIT_HASH_TIMEOUT_MS = 4_000;
const GIT_HASH_MAX_BUFFER = 1024 * 1024;

export function createWorkspaceChangeScanner(): WorkspaceChangeScanner {
	return {
		async snapshot(cwd) {
			try {
				// 신규 디렉토리가 "?? dir/"로 접히지 않도록 미추적 파일을 개별 열거한다.
				const { stdout } = await execFileAsync("git", ["status", "--porcelain=v1", "-z", "--untracked-files=all"], {
					cwd,
					encoding: "utf8",
					maxBuffer: GIT_STATUS_MAX_BUFFER,
					timeout: GIT_STATUS_TIMEOUT_MS,
					// 콘솔 없는 호스트에서 git.exe 실행 시 콘솔 창이 깜빡이는 것을 방지한다(방어적).
					windowsHide: true,
				});
				const entries = parseGitStatusPorcelainZ(stdout);
				if (!entries) return null;
				return await enrichEntriesWithContentHashes(cwd, entries);
			} catch {
				return null;
			}
		},
	};
}

export function parseGitStatusPorcelainZ(output: string): WorkspaceChangeSnapshotEntry[] | null {
	try {
		const tokens = output.split("\0").filter((token) => token.length > 0);
		const entries: WorkspaceChangeSnapshotEntry[] = [];
		for (let i = 0; i < tokens.length; i++) {
			const token = tokens[i]!;
			if (token.length < 4) continue;
			const status = token.slice(0, 2).trim();
			const path = token.slice(3);
			if (!status || !path) continue;
			if (status.startsWith("R") || status.startsWith("C")) {
				const oldPath = tokens[++i];
				if (!oldPath) return null;
				entries.push({ status, path: `${oldPath} -> ${path}` });
			} else {
				entries.push({ status, path });
			}
		}
		return entries;
	} catch {
		return null;
	}
}

async function enrichEntriesWithContentHashes(
	cwd: string,
	entries: readonly WorkspaceChangeSnapshotEntry[],
): Promise<WorkspaceChangeSnapshotEntry[]> {
	const hashPaths = entries.map((entry) => hashablePath(entry)).filter((path): path is string => Boolean(path));
	if (hashPaths.length === 0) return [...entries];

	try {
		const stdout = await execGitHashObject(cwd, hashPaths);
		const hashes = stdout.split(/\r?\n/).filter((line) => line.length > 0);
		if (hashes.length !== hashPaths.length) return [...entries];
		const hashByPath = new Map(hashPaths.map((filePath, index) => [filePath, hashes[index]!]));
		return entries.map((entry) => {
			const contentHash = hashByPath.get(hashablePath(entry) ?? "");
			return contentHash ? { ...entry, contentHash } : entry;
		});
	} catch {
		return [...entries];
	}
}

function execGitHashObject(cwd: string, paths: readonly string[]): Promise<string> {
	return new Promise((resolve, reject) => {
		const child = execFile(
			"git",
			["hash-object", "--stdin-paths"],
			{
				cwd,
				encoding: "utf8",
				maxBuffer: GIT_HASH_MAX_BUFFER,
				timeout: GIT_HASH_TIMEOUT_MS,
				// 콘솔 없는 호스트에서 git.exe 실행 시 콘솔 창이 깜빡이는 것을 방지한다(방어적).
				windowsHide: true,
			},
			(error, stdout) => {
				if (error) {
					reject(error);
					return;
				}
				resolve(stdout);
			},
		);
		child.stdin?.end(`${paths.join("\n")}\n`);
	});
}

function hashablePath(entry: WorkspaceChangeSnapshotEntry): string | null {
	if (entry.status === "D") return null;
	const path = entry.path.includes(" -> ") ? entry.path.split(" -> ").at(-1)! : entry.path;
	if (!path || path.includes("\n")) return null;
	return path;
}
